import React, { FC, useCallback, useEffect, useState } from 'react';
import { Loading } from '../../shared/components';
import { useCachedFetch } from '../../shared/hooks';
import { ITrajectoryDetail } from '../../../config/api/models/Trajectory';
import apiRoutes from '../../../config/api/routes';
import { useParams } from 'react-router';
import { TrajectoryViewDetail } from './TrajectoryViewDetail';
import { useUser } from '../../shared';
import { useHistory } from 'react-router-dom';
import { useI18nPath } from '../../../config/clientRoutes/hooks';
import { PublishUnPublishHandler } from './PublishUnPublishHandler';
import { UrlParams } from '../../shared/types';
import { useAppDispatch, useAppSelector } from '../../shared/hooks/useAppDispatch';
import { setHoverBlock } from '../../shared/slices/blocks/entity/BlocksSlice';
import { setHasSubheader } from 'src/modules/shared/slices/filters/entity/FiltersSlice';
import { setTrajectory } from 'src/modules/shared/slices/trajectories/entity/trajectorySlice';
import { setTeacherViewMode } from 'src/modules/shared/slices/teacher/entity/teacherViewModeSlice';
import { TrajectoryNavBar } from './Navigation/TrajectoryNavBar';

const TrajectoryView: FC = () => {
  // Hooks
  const { isStudent, isAuthor } = useUser();
  const history = useHistory();
  const { getPath } = useI18nPath();
  const { bookId, trajectoryId } = useParams<UrlParams>();

  // State
  const [publishTrigger, setPublishTrigger] = useState(-1);
  const [unPublishTrigger, setUnPublishTrigger] = useState(-1);

  const [initialized, setInitialized] = useState(isStudent);
  const dispatch = useAppDispatch();

  // Redux
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const loading = useAppSelector((state) => state.trajectories.loading);

  const trajectoryDetailsFetch = useCachedFetch<{ data: ITrajectoryDetail }>(
    ['trajectoryDetails', trajectoryId],
    apiRoutes.trajectory(trajectoryId as string)
  );
  const refetchTrajectoryDetails = trajectoryDetailsFetch.refetch;

  const handlePublish = useCallback(() => setPublishTrigger(publishTrigger + 1), [publishTrigger]);
  const handleUnPublish = useCallback(
    () => setUnPublishTrigger(unPublishTrigger + 1),
    [unPublishTrigger]
  );
  const refetchTrajectoryDetailsCallback = useCallback(() => {
    refetchTrajectoryDetails().then();
  }, [refetchTrajectoryDetails]);

  useEffect(() => {
    if (viewMode === 'read') dispatch(setHoverBlock(undefined));
    viewMode && dispatch(setTeacherViewMode(viewMode));
  }, [viewMode, dispatch]);

  useEffect(() => {
    dispatch(setHasSubheader(true));
    return () => {
      dispatch(setHasSubheader(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!trajectoryDetailsFetch.data?.data) return;
    if (trajectoryDetailsFetch.data.data) {
      dispatch(setTrajectory(trajectoryDetailsFetch.data.data));
      if (trajectoryDetailsFetch.data.data.isDraft) {
        if (!isAuthor) {
          history.push(
            getPath('bookOverview', {
              bookId,
            })
          );
        }
      }
    }
  }, [trajectoryDetailsFetch, isAuthor, history, getPath, bookId, dispatch]);

  useEffect(() => {
    if (loading) return;

    setInitialized(true);
  }, [loading]);

  if (trajectoryDetailsFetch.isSuccess && !trajectoryDetailsFetch.data?.data)
    return <div>Not a valid trajectory.</div>;

  if (!initialized || loading) return <Loading />;

  return initialized && trajectoryDetailsFetch.data?.data ? (
    <>
      <PublishUnPublishHandler
        onPatchDraft={refetchTrajectoryDetailsCallback}
        trajectory={trajectoryDetailsFetch.data.data}
        publishTrigger={publishTrigger}
        unPublishTrigger={unPublishTrigger}
      />
      <TrajectoryNavBar />
      <TrajectoryViewDetail
        trajectory={trajectoryDetailsFetch.data.data}
        refetchTrajectoryDetailsCallback={refetchTrajectoryDetailsCallback}
        onPublish={handlePublish}
        onUnPublish={handleUnPublish}
      />
    </>
  ) : null;
};

export default TrajectoryView;
